import React, { useState, useEffect } from 'react';
import configService from '../services/configService';
import PerformanceInsights from './PerformanceInsights';

const DashboardOverview = ({ businessConfig }) => {
  const [usageData, setUsageData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [timeRange, setTimeRange] = useState('7days');
  const [cachedBusinessName, setCachedBusinessName] = useState(() => {
    try {
      return localStorage.getItem('cached_business_name');
    } catch {
      return null;
    }
  });
  
  useEffect(() => {
    loadUsageData();
    loadSubscriptionData();
  }, []);

  useEffect(() => {
    if (businessConfig?.business_name) {
      localStorage.setItem('cached_business_name', businessConfig.business_name);
      setCachedBusinessName(businessConfig.business_name);
    }
  }, [businessConfig]);

  const loadUsageData = async () => {
    try {
      const data = await configService.getUsageData();
      setUsageData(data);
    } catch (error) {
      console.error('Failed to load usage data:', error);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      const data = await configService.getSubscriptionStatus();
      setSubscriptionData(data);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    }
  };

  const calculateDiscountedPrice = (basePrice, discount) => {
    if (!discount) return basePrice;
    if (discount.percent_off) {
      return basePrice * (1 - discount.percent_off / 100);
    }
    if (discount.amount_off) {
      return Math.max(0, basePrice - discount.amount_off / 100);
    }
    return basePrice;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/^\+1/, '').replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const performanceData = {
    totalConversations: 247,
    revenueGenerated: 12450,
    revenuePerCall: 50.4,
    successRate: 87.3,
    trendingRevenue: [
      { date: '2024-01-01', revenue: 1200 },
      { date: '2024-01-02', revenue: 1450 },
      { date: '2024-01-03', revenue: 1100 },
      { date: '2024-01-04', revenue: 1800 },
      { date: '2024-01-05', revenue: 2100 },
      { date: '2024-01-06', revenue: 1900 },
      { date: '2024-01-07', revenue: 2300 }
    ],
    successCriteria: [
      { name: 'Appointments Booked', value: 89, target: 100 },
      { name: 'Lead Qualification', value: 92, target: 85 },
      { name: 'Customer Satisfaction', value: 4.7, target: 4.5 }
    ]
  };

  return (
    <div className="space-y-6 max-w-full">
      <div className="mb-6">
        <div className="text-4xl font-bold text-gray-900 mb-2">Welcome back!</div>
        <p className="text-sm text-gray-600" style={{fontFamily: 'Montserrat, sans-serif'}}>Your AI assistant is ready to help your business.</p>
      </div>

      <div
        className="p-6 relative overflow-hidden rounded-lg shadow-lg"
        style={{
          background: 'linear-gradient(95deg, #055EFD 0%, #1443E0 100%)'
        }}
      >
        <div
          className="absolute"
          style={{
            left: '89px',
            top: '-5px'
          }}
        >
          <svg viewBox="0 0 328 328" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="0.33" className="w-full h-auto max-w-[328px]">
            <path d="M252.751 207.808L328 207.735L149.175 -120L108.787 -56.046C108.362 -55.3976 107.949 -54.7368 107.548 -54.0761L107.172 -53.4766C96.6285 -35.5764 95.4624 -12.7086 106.127 6.83123C109.881 13.7075 114.751 19.556 120.4 24.2788C120.594 24.4501 120.801 24.6091 121.007 24.7682C124.153 27.3499 142.543 37.7499 154.423 49.2511C167.189 61.621 175.753 76.9395 176.469 78.2487C177.174 79.5457 185.433 95.0356 188.956 112.532C192.236 128.793 191.094 150.021 191.568 154.071C191.592 154.328 191.628 154.585 191.653 154.842C192.588 162.183 194.872 169.463 198.625 176.339C209.29 195.879 229.077 207.16 249.714 207.772L250.431 207.784C251.196 207.796 251.973 207.796 252.739 207.784L252.751 207.808Z" fill="#310DA6"/>
            <path d="M131.353 171.839C150.268 137.79 138.199 94.7424 104.396 75.6897C70.5938 56.637 27.858 68.7938 8.94317 102.843C-9.97164 136.892 2.09722 179.939 35.8997 198.992C69.7022 218.044 112.438 205.887 131.353 171.839Z" fill="#310DA6"/>
          </svg>
        </div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-8">
          <div className="flex-1 flex flex-col">
            <h2 className="!text-white text-xl font-bold mb-2" style={{fontFamily: 'Montserrat, sans-serif', lineHeight: '28px'}}>
              {businessConfig?.business_name || cachedBusinessName || <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />}
            </h2>
            <div className="flex items-center gap-2">
              <div 
                className="w-2.5 h-2.5 rounded-full"
                style={{
                  background: '#359E16',
                  outline: '2px white solid'
                }}
              />
              <p className="!text-[#F9F9FB] text-sm font-medium">
                Your assistant is ready to take calls
              </p>
            </div>
          </div>
          <div className="flex flex-col md:items-end">
            <p className="!text-white text-sm font-medium">
              Assistant Phone Number
            </p>
            <p className="!text-white text-lg font-bold" style={{fontFamily: 'Montserrat, sans-serif', lineHeight: '24px'}}>
              {businessConfig?.assistant_phone ? formatPhoneNumber(businessConfig.assistant_phone) : <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />}
            </p>
          </div>
        </div>
      </div>

      {/* Current Billing Cycle / Prepaid Balance */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {usageData?.payment_type === 'prepaid' ? 'Prepaid Balance' : 'Current Billing Cycle'}
        </h3>
        {!usageData ? (
          <div className="flex items-center justify-center py-8">
            <span className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin inline-block" />
          </div>
        ) : usageData.plan_minutes_included === 0 ? (
          // Pay-as-you-go plan
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b">
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {usageData.subscription_plan}
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Pay As You Go</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  ${usageData.monthly_payment.toFixed(2)}/mo + ${usageData.overage_rate.toFixed(2)}/min
                </div>
              </div>
            </div>
            {subscriptionData?.discount && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-green-700 text-sm font-medium">🎉 Discount Applied</span>
                </div>
                <div className="text-xs text-green-600">
                  {subscriptionData.discount.coupon_name || 'Promotional discount'}
                  {subscriptionData.discount.percent_off && ` - ${subscriptionData.discount.percent_off}% off`}
                  {subscriptionData.discount.amount_off && ` - $${(subscriptionData.discount.amount_off / 100).toFixed(2)} off`}
                  {subscriptionData.discount.duration === 'repeating' && subscriptionData.discount.duration_in_months && 
                    ` for ${subscriptionData.discount.duration_in_months} months`}
                  {subscriptionData.discount.duration === 'once' && ' (one-time)'}
                  {subscriptionData.discount.duration === 'forever' && ' (forever)'}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Minutes Used</div>
                <div className="text-2xl font-bold text-gray-900">{usageData.total_assistant_minutes.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Usage Charges</div>
                <div className="text-2xl font-bold text-gray-900">${usageData.mtd_overage_total.toFixed(2)}</div>
              </div>
            </div>
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Month-to-Date Total</span>
                {subscriptionData?.discount ? (
                  <div className="text-right">
                    <div className="text-sm text-gray-500 line-through">
                      ${(usageData.monthly_payment + usageData.mtd_overage_total).toFixed(2)}
                    </div>
                    <div className="text-xl font-bold text-green-600">
                      ${(calculateDiscountedPrice(usageData.monthly_payment, subscriptionData.discount) + usageData.mtd_overage_total).toFixed(2)}
                    </div>
                  </div>
                ) : (
                  <span className="text-xl font-bold text-gray-900">${(usageData.monthly_payment + usageData.mtd_overage_total).toFixed(2)}</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Regular plan with included minutes OR Prepaid plan
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {usageData.payment_type === 'prepaid' 
                  ? `${usageData.remaining_minutes}/${usageData.plan_minutes_included}` 
                  : `${usageData.total_assistant_minutes || 0}/${usageData.plan_minutes_included}`
                }
              </div>
              <div className="text-sm text-gray-600">
                {usageData.payment_type === 'prepaid' ? 'remaining / prepaid minutes' : 'minutes used / plan limit'}
              </div>
            </div>
            <div className="w-24 h-24">
              <div className="relative w-full h-full">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-blue-500"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={
                      usageData.payment_type === 'prepaid'
                        ? `${((usageData.remaining_minutes / usageData.plan_minutes_included) * 100)}, 100`
                        : `${((usageData.total_assistant_minutes / usageData.plan_minutes_included) * 100)}, 100`
                    }
                    strokeLinecap="round"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-semibold text-gray-700">
                    {usageData.payment_type === 'prepaid'
                      ? `${Math.round((usageData.remaining_minutes / usageData.plan_minutes_included) * 100)}%`
                      : `${Math.round((usageData.total_assistant_minutes / usageData.plan_minutes_included) * 100)}%`
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default DashboardOverview;
